import { createContext } from 'preact'
import { useContext, useState, useEffect } from 'preact/hooks'
import { useOmneoClient } from '../hooks/useOmneoClient'
import { useCacheConfig } from '../hooks/useCacheConfig'
import { useQueryState } from '@/components/hooks/useQueryState'

/**
 *  @typedef {import("../types/omneo").AccountLayoutContext} AccountLayoutContext
 */

/**
 * @type {createContext<{AccountLayoutContext}>}
 */
const AccountLayoutContext = createContext({
  handlePushState: (nextState) => {},
  handPushChildState: (state, child) => {},
  updateCustomer: (customerDetails) => {},
  omneoClient: null,
  config: {
    path: '/',
    modal: {
      path: '',
      data: ''
    },
    childPath: '',
    customer: {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      acceptsMarketing: false,
      orders: [
        {
          id: '',
          name: '',
          itemCount: 0,
          lineItems: [
            {
              id: '',
              title: '',
              quantity: '',
              image: '',
              productId: '',
              variantId: ''
            }
          ]
        }
      ]
    }
  },
  cacheConfig: {
    updateCacheConfig: (key, string) => {},
    cacheData: {}
  }
})

export const AccountLayoutContextProvider = ({ children }) => {
  const [config, setConfig] = useState({
    path: '/',
    childPath: '',
    modal: {
      path: '',
      data: ''
    },
    customer: {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      acceptsMarketing: false,
      orders: [
        {
          id: '',
          name: '',
          itemCount: 0,
          lineItems: [
            {
              id: '',
              title: '',
              quantity: '',
              image: '',
              productId: '',
              variantId: ''
            }
          ]
        }
      ]
    }
  })

  const [section, setSection] = useQueryState('section')
  const [child, setChild] = useQueryState('child')
  const [isEdit, setIsEdit] = useQueryState('edit')

  const { client } = useOmneoClient(true)
  const { cacheConfig, handleSetCacheConfig } = useCacheConfig()

  /**
   * Fetches the user data and sets it globally
   */
  const getUserData = async () => {
    const profile = await client.profileClient.get()
    const rewards = await client.profileClient.getRewards()
    const balance = await client.profileClient.getBalance()
    const wishlist = await client.profileClient.syncMyWishlist(profile)

    handleSetCacheConfig('Profiles', { ...profile })
    handleSetCacheConfig('Rewards', [...rewards])
    handleSetCacheConfig('Balance', { ...balance })
  }

  const updateCustomer = (customerDetails) => {
    setConfig((c) => ({
      ...c,
      customer: customerDetails
    }))
  }

  /** On initial load / render */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.has('section')) {
      const section = params.get('section')
      if (params.has('child')) {
        const child = params.get('child')
        handPushChildState(section, child)
        return
      }

      handlePushState(params.get('section'), false, false)
    }
  }, [])

  const handleParam = (section, { child } = {}) => {
    setSection(section)
    setChild(child)
    setIsEdit(undefined)
    document?.querySelector('#top')?.scrollIntoView({ behavior: 'instant' })
  }

  const handPushChildState = (state, child) => {
    setConfig((c) => ({
      ...c,
      path: state,
      childPath: child
    }))
    handleParam(state, { child })
  }

  const handlePushState = (nextState, force = false, reload = false) => {
    // Reset other states
    setIsEdit(undefined)
    setChild(undefined)
    window.dispatchEvent(new PopStateEvent('popstate'))

    if (config.path === nextState && !force) return

    if (config.modal.path === 'kids-profiles') {
      client.reSync().then(() => {
        setConfig((c) => ({
          ...c,
          path: nextState,
          childPath: ''
        }))
        handleParam(nextState)
      })
    } else {
      setConfig((c) => ({
        ...c,
        path: nextState,
        childPath: ''
      }))
      handleParam(nextState)
    }
  }

  if (!client) return <div class="h-dvh w-dvw bg-slate-300"></div>
  return (
    <AccountLayoutContext.Provider
      value={{
        handlePushState,
        config,
        handPushChildState,
        updateCustomer,
        omneoClient: client,
        cacheConfig: {
          updateCacheConfig: handleSetCacheConfig,
          cacheData: cacheConfig,
          getUserData
        }
      }}
    >
      {children}
    </AccountLayoutContext.Provider>
  )
}

/**
 * @return {AccountLayoutContext}
 */
export const useAccountLayoutContext = () => {
  const config = useContext(AccountLayoutContext)
  return config
}
